import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { listTutorClueLog } from "./AdminTutorService";

function formatWhen(value) {
    if (!value) {
        return "—";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString();
}

export default function AdminTutorBedrockLog({ cloudUserId, showUser = true, topSpacing = 3 }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [testAccountFilter, setTestAccountFilter] = useState("exclude");
    const [excludeStaff, setExcludeStaff] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const list = await listTutorClueLog(cloudUserId);
                if (!cancelled) {
                    setRows(list);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Could not load Bedrock clue history.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [cloudUserId]);

    const visible = useMemo(() => {
        return rows.filter((row) => {
            if (testAccountFilter === "exclude" && row.testAccount) {
                return false;
            }
            if (testAccountFilter === "include" && !row.testAccount) {
                return false;
            }
            if (excludeStaff && row.staff) {
                return false;
            }
            return true;
        });
    }, [excludeStaff, rows, testAccountFilter]);

    return (
        <Box sx={{ mt: topSpacing }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                Bedrock clue history
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                One row each time a word was sent to Bedrock. Words that already had a clue are not listed.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="tutor-history-test-filter">Test accounts</InputLabel>
                    <Select
                        labelId="tutor-history-test-filter"
                        label="Test accounts"
                        value={testAccountFilter}
                        onChange={(event) => setTestAccountFilter(event.target.value)}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="exclude">Exclude tester</MenuItem>
                        <MenuItem value="include">Include tester</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={excludeStaff}
                            onChange={(event) => setExcludeStaff(event.target.checked)}
                            color="primary"
                        />
                    }
                    label="Exclude staff"
                />
            </Box>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>When</TableCell>
                            {showUser ? <TableCell>User</TableCell> : null}
                            <TableCell>Puzzle</TableCell>
                            <TableCell>Word</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={showUser ? 4 : 3}>
                                    {rows.length === 0
                                        ? "No Bedrock clue requests yet."
                                        : "No matching history."}
                                </TableCell>
                            </TableRow>
                        ) : (
                            visible.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{formatWhen(row.createdAt)}</TableCell>
                                    {showUser ? (
                                        <TableCell>
                                            <Box>{row.ownerName || "—"}</Box>
                                            {row.ownerEmail ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.ownerEmail}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                    ) : null}
                                    <TableCell>{row.storyTitle || row.storyId || "—"}</TableCell>
                                    <TableCell>{row.word}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}
